import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// httpOnly cookie auth — `withCredentials` makes the browser attach the
// `access_token` cookie automatically. We deliberately do NOT read/write any
// auth token from JavaScript so an XSS payload cannot exfiltrate the session.
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// 402 Payment Required → trigger app-wide paywall navigation.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 402 && typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path !== "/subscribe" && !path.startsWith("/login") && !path.startsWith("/register")) {
        window.location.assign("/subscribe");
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(", ");
  // Structured error response — {message, parsing_report?, type?}
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
  }
  return String(detail);
}

/** Pull the most informative error message out of an axios error object.
 *  Distinguishes between:
 *    • backend 4xx/5xx with structured body  → uses `formatApiError(detail)`
 *    • network errors (ERR_NETWORK / CORS / DNS) → "Network error: ..."
 *    • timeouts (ECONNABORTED, timeout)          → "Request timed out..."
 *  Frontend toast handlers should always go through this helper instead of
 *  the bare `formatApiError(e.response?.data?.detail)` so the user gets a
 *  real reason instead of "Something went wrong" when there is no response.
 */
export function describeAxiosError(err, fallback = "Operation failed") {
  if (!err) return fallback;
  // Server returned a structured error.
  if (err.response?.data?.detail) {
    return formatApiError(err.response.data.detail);
  }
  // slowapi rate-limit middleware uses `{error: "Rate limit exceeded: 3 per 1 hour"}`
  // — different key from FastAPI's HTTPException. Surface either shape.
  if (err.response?.data?.error) {
    return formatApiError(err.response.data.error);
  }
  if (err.response?.data?.message) {
    return formatApiError(err.response.data.message);
  }
  if (err.code === "ECONNABORTED" || /timeout/i.test(err.message || "")) {
    return (
      "The request took too long and was cancelled by the browser. " +
      "Heavy operations (OCR / AI on large files) can exceed the proxy's " +
      "timeout. Try a smaller file or disable OCR / AI to retry."
    );
  }
  if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
    return (
      "Network error — the request never reached the server (or the response " +
      "was blocked by CORS / a proxy). Check your connection and try again."
    );
  }
  if (err.response?.status === 429) {
    return "Too many attempts in a short time — please wait an hour and try again.";
  }
  if (err.response?.status >= 500) {
    return `Server error (HTTP ${err.response.status}). ${err.response.statusText || ""}`.trim();
  }
  return err.message || fallback;
}

export const formatINR = (n) => {
  if (n == null || isNaN(n)) return "0.00";
  return Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default api;
